// Implements: F-IDN-026
/**
 * "Invitar personas" (v3 `openInvite`): the addresses, the role and, for a team role, the team.
 * The form is checked here before anything is sent: no address, an address that is not one, or a
 * team role with no team is said in one line under the form and sends nothing. The server still
 * decides every invitation.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Select } from "../../../shared/ui/atoms/Select";
import { Textarea } from "../../../shared/ui/atoms/Textarea";
import { Field, hintId } from "../../../shared/ui/molecules/Field";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import type { InvitationRole } from "../../../types/organization";
import { parseInvitationEmails } from "../model/invitationEmails";
import type { InvitationSeat } from "../model/roles";
import { DEFAULT_INVITATION_ROLE, INVITATION_ROLES, asRole, invitationSeat, isTeamRole, roleLabelKey } from "../model/roles";
import styles from "./Organization.module.css";

/** The addresses and the seat they are invited into: `role` is always an organization role. */
export interface InviteRequest extends InvitationSeat {
  readonly emails: readonly string[];
}

export interface InviteDialogProps {
  readonly teams: readonly { readonly id: string; readonly name: string }[];
  readonly pending: boolean;
  /** Hidden while another question is asked on top of it; what was typed stays (owner 2026-10-19). */
  readonly hidden?: boolean;
  readonly onSubmit: (request: InviteRequest) => void;
  readonly onCancel: () => void;
}

export function InviteDialog({ teams, pending, hidden = false, onSubmit, onCancel }: InviteDialogProps): ReactElement {
  const { t } = useTranslation();
  const base = useId();
  const emailsId = `${base}-emails`;
  const roleId = `${base}-role`;
  const teamId = `${base}-team`;
  const [raw, setRaw] = useState("");
  const [role, setRole] = useState<InvitationRole>(DEFAULT_INVITATION_ROLE);
  const [team, setTeam] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const teamRole = isTeamRole(role);

  function submit(): void {
    if (pending) return;
    const { valid, invalid } = parseInvitationEmails(raw);
    if (valid.length === 0 && invalid.length === 0) {
      setProblem(t("organization.invite.noEmails"));
      return;
    }
    if (invalid.length > 0) {
      setProblem(t("organization.invite.badEmails", { emails: invalid.join(", ") }));
      return;
    }
    if (teamRole && team === "") {
      setProblem(t("organization.invite.teamRequired"));
      return;
    }
    setProblem(null);
    onSubmit({ emails: valid, ...invitationSeat(role, team) });
  }

  return (
    <Dialog
      open={!hidden}
      title={t("organization.invite.title")}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t("common.action.cancel")}
          </Button>
          <Button variant="primary" loading={pending} onClick={submit}>
            {t("organization.invite.submit")}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field id={emailsId} label={t("organization.invite.emails")} hint={t("organization.invite.emailsHelp")}>
          <Textarea
            id={emailsId}
            value={raw}
            rows={3}
            placeholder={t("organization.invite.emailsPlaceholder")}
            describedBy={hintId(emailsId)}
            invalid={problem !== null}
            onChange={setRaw}
          />
        </Field>
        <div className={styles.two}>
          <Field id={roleId} label={t("organization.invite.role")}>
            <Select
              id={roleId}
              value={role}
              options={INVITATION_ROLES.map((option) => ({ value: option, label: t(roleLabelKey(option)) }))}
              onChange={(value) => {
                const next = asRole(value, INVITATION_ROLES);
                if (next !== null) setRole(next);
              }}
            />
          </Field>
          <Field id={teamId} label={t("organization.invite.team")} hint={teamRole ? undefined : t("organization.invite.teamHelp")}>
            <Select
              id={teamId}
              value={teamRole ? team : ""}
              disabled={!teamRole}
              describedBy={teamRole ? undefined : hintId(teamId)}
              options={[
                { value: "", label: t("organization.invite.noTeam") },
                ...teams.map((option) => ({ value: option.id, label: option.name })),
              ]}
              onChange={setTeam}
            />
          </Field>
        </div>
        {problem === null ? null : (
          <p className={styles.problem} role="alert">
            {problem}
          </p>
        )}
      </div>
    </Dialog>
  );
}
